/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { Calendar, ArrowDownRight } from 'lucide-react';
import { Link } from '@/components/link';
import { cn } from '@/lib/utils';
import { widenImageSrcSet, widenImageUrl, DEFAULT_WIDEN_FALLBACK_WIDTH } from '@/lib/images/widen-image';
import { formatDateMedium } from '@/lib/date-utils';
import type { LearnArticle } from '@/lib/learn/catalog.server';
import { learnArticleUrl } from '@/lib/learn/urls';

/** Max characters shown for the tile description before truncating; trailing "…" is added. (UCR-2738) */
const MAX_DESCRIPTION_LENGTH = 29;

/** Cap the description at 29 chars + ellipsis. Trailing whitespace is trimmed before the "…". */
function truncateDescription(text: string): string {
    if (text.length <= MAX_DESCRIPTION_LENGTH) return text;
    return `${text.slice(0, MAX_DESCRIPTION_LENGTH).trimEnd()}…`;
}

/** Layout variants — applied as the `article-tile--<layout>` CSS class (see theme/article-tile.css). */
export type ArticleTileLayout = 'stacked' | 'side-by-side';

export interface ArticleTileProps {
    article: LearnArticle;
    /**
     * Layout variant. `stacked` (image over copy) is the Article Listing default; `side-by-side`
     * (image | copy on desktop, stacked on mobile) is for the featured-articles component.
     */
    layout?: ArticleTileLayout;
    /** Responsive `sizes` hint for the thumbnail. Defaults to a 3-up listing grid. */
    sizes?: string;
    className?: string;
}

/**
 * Reusable Learn article tile (UCR-2738) — Widen thumbnail, title, 29-char-capped description, the
 * page's creation date, and a "Read More" link. The whole card is the link; layout is switched by
 * the CSS modifier class so a Page Designer component can flip stacked ⇄ side-by-side.
 */
export default function ArticleTile({
    article,
    layout = 'stacked',
    sizes = '(min-width: 1024px) 33vw, (min-width: 640px) 50vw, 100vw',
    className,
}: ArticleTileProps): ReactElement {
    const { i18n } = useTranslation();
    const fileURL = article.thumbnail?.fileURL;
    // Capped summary (29 chars + ellipsis) used by the stacked tile and by side-by-side on mobile,
    // where it collapses to a stacked card. Side-by-side on desktop shows the full summary — the
    // capped/full swap is done in CSS (below) so it stays responsive without a viewport read.
    const capped = article.description ? truncateDescription(article.description) : '';
    const formattedDate = formatDateMedium(article.date, i18n.language);

    return (
        <Link
            to={learnArticleUrl(article)}
            aria-label={`Read the article ${article.title}`}
            className={cn('article-tile', `article-tile--${layout}`, className)}>
            <div className="article-tile__media">
                {fileURL ? (
                    <img
                        src={widenImageUrl(fileURL, { w: DEFAULT_WIDEN_FALLBACK_WIDTH })}
                        srcSet={widenImageSrcSet(fileURL)}
                        sizes={sizes}
                        alt={article.thumbnail?.alternativeText || article.title}
                        loading="lazy"
                        className="article-tile__img"
                    />
                ) : null}
            </div>
            <div className="article-tile__body">
                <h3 className="article-tile__title">{article.title}</h3>
                {/* Description + meta travel together and sit at the bottom of the copy block, so a
                    taller title in a sibling card only opens the gap beneath its own title — every
                    card's description and date/CTA row stay on the same baseline. (UCR-3027) */}
                <div className="article-tile__foot">
                    {article.description ? (
                        layout === 'side-by-side' ? (
                            <p className="article-tile__desc">
                                {/* Capped on mobile (card stacks), full on desktop — swapped in CSS. */}
                                <span className="article-tile__desc-capped">{capped}</span>
                                <span className="article-tile__desc-full">{article.description}</span>
                            </p>
                        ) : (
                            <p className="article-tile__desc">{capped}</p>
                        )
                    ) : null}
                    <div className="article-tile__meta">
                        {formattedDate ? (
                            <span className="article-tile__date">
                                <Calendar className="article-tile__icon" aria-hidden="true" />
                                {formattedDate}
                            </span>
                        ) : null}
                        {/* Visible affordance; the accessible name lives on the card link's aria-label. */}
                        <span className="article-tile__cta" aria-hidden="true">
                            <span className="article-tile__cta-label">Read More</span>
                            <ArrowDownRight className="article-tile__icon" />
                        </span>
                    </div>
                </div>
            </div>
        </Link>
    );
}
